// ─────────────────────────────────────────────────────────────────────────
// VentasPorCliente — concentración de la facturación (26-ago-2026, pedido Maxi).
//
// Responde tres preguntas de estudio:
//   1. ¿Cuánto estamos facturando últimamente, mes a mes?
//   2. ¿Cuánto pertenece a Rocmin y cuánto a otros clientes? (riesgo de
//      concentración — Rocmin ≈80-90% de las ventas)
//   3. ¿Qué clientes dejaron de comprar? (última compra hace N meses)
// Y el PERFIL del cliente: expandir uno muestra su facturación por mes y
// todos sus documentos (facturas 33 / NCs 61) con la OT vinculada.
//
// Fuente: colección factura (SII sync). NCs restan (misma convención H-4).
// ─────────────────────────────────────────────────────────────────────────
import React, { useEffect, useState } from "react";
import backend from "../../backend";
import { formatPrice } from "../../utils";

const MESES_CORTOS = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];
const labelPeriodo = p => {
  const [y, m] = p.split("-");
  return `${MESES_CORTOS[Number(m) - 1]} ${y.slice(2)}`;
};
const fmtCLP = n => `${n < 0 ? "−" : ""}$${formatPrice(Math.round(Math.abs(n || 0)))}`;
const num = { fontVariantNumeric: "tabular-nums" };

// Meses transcurridos desde una fecha (aprox por mes calendario).
const mesesDesde = fecha => {
  if (!fecha) return null;
  const d = new Date(fecha), hoy = new Date();
  return (hoy.getFullYear() - d.getFullYear()) * 12 + (hoy.getMonth() - d.getMonth());
};

// Barras por mes (compartidas entre el resumen y el perfil de cada cliente).
const BarrasMensuales = ({ periodos, valores, color = "#2ecc71", height = 56 }) => {
  const max = Math.max(...periodos.map(p => Math.abs(valores[p] || 0)), 1);
  return (
    <div className="chips-scroll" style={{ display: "flex", gap: 4, alignItems: "flex-end", overflowX: "auto", paddingBottom: 2 }}>
      {periodos.map(p => {
        const v = valores[p] || 0;
        const h = Math.max(Math.round(Math.abs(v) / max * height), v !== 0 ? 3 : 1);
        return (
          <div key={p} style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3, minWidth: 34, flexShrink: 0 }}
            title={`${labelPeriodo(p)}: ${fmtCLP(v)}`}>
            <div style={{ fontSize: 9, color: "#8a8a8a", ...num }}>{v !== 0 ? `${Math.round(v / 1e6)}M` : ""}</div>
            <div style={{
              width: 22, height: h, borderRadius: 3,
              background: v < 0 ? "#e74c3c" : color, opacity: v === 0 ? 0.25 : 1,
              alignSelf: "center",
            }} />
            <div style={{ fontSize: 9, color: "#8a8a8a", whiteSpace: "nowrap" }}>{labelPeriodo(p)}</div>
          </div>
        );
      })}
    </div>
  );
};

const S = {
  card: { background: "#232323", border: "1px solid #2e2e2e", borderRadius: 12, padding: 14 },
  label: { fontSize: 10, fontWeight: 700, color: "#8a8a8a", textTransform: "uppercase", letterSpacing: ".05em" },
};

// ── Perfil expandido de un cliente: sus docs de venta ──
const PerfilCliente = ({ cliente, periodos }) => {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let vivo = true;
    backend.get(`sii/ventas-clientes/${encodeURIComponent(cliente.rut)}`)
      .then(res => { if (vivo) setDocs(res.data || []); })
      .catch(e => { if (vivo) setError(e?.response?.data?.error || e.message); });
    return () => { vivo = false; };
  }, [cliente.rut]);

  return (
    <div style={{ borderTop: "1px solid #2e2e2e", padding: "12px 14px", background: "#1f1f1f" }}>
      <div style={{ ...S.label, marginBottom: 8 }}>Facturación por mes</div>
      <BarrasMensuales periodos={periodos} valores={cliente.porMes} />

      <div style={{ ...S.label, margin: "14px 0 6px" }}>Documentos ({cliente.nDocs})</div>
      {error && <div style={{ color: "#e74c3c", fontSize: 12 }}>{error}</div>}
      {!docs && !error && <div style={{ color: "#8a8a8a", fontSize: 12 }}>Cargando…</div>}
      {docs && (
        <div style={{ display: "flex", flexDirection: "column" }}>
          {docs.map((f, i) => {
            const esNC = f.tipoDoc === 61;
            return (
              <div key={i} style={{
                display: "flex", alignItems: "center", gap: 8, padding: "7px 0",
                borderBottom: "1px solid #282828", fontSize: 12, minHeight: 36,
              }}>
                <span style={{
                  fontSize: 10, fontWeight: 700, borderRadius: 4, padding: "1px 6px", flexShrink: 0,
                  color: esNC ? "#e74c3c" : "#3498db",
                  border: `1px solid ${esNC ? "#5a2020" : "#1a4a6e"}`,
                }}>
                  {esNC ? "NC" : f.tipoDoc === 33 ? "F" : f.tipoDoc} {f.folio}
                </span>
                <span style={{ color: "#8a8a8a", flexShrink: 0, ...num }}>
                  {f.fecha ? new Date(f.fecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" }) : "—"}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: "#b8b8b8", fontSize: 11 }}>
                  {f.workOrder ? `OT ${f.workOrder.number} · ${f.workOrder.name || ""}` : (f.ocCliente ? `OC ${f.ocCliente}` : "")}
                </span>
                <span style={{ fontWeight: 700, color: esNC ? "#e74c3c" : "#2ecc71", flexShrink: 0, ...num }}>
                  {esNC ? "−" : ""}{fmtCLP(f.montoTotal)}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

const VentasPorCliente = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [meses, setMeses] = useState(12);
  const [abierto, setAbierto] = useState(null); // rut expandido

  useEffect(() => {
    let vivo = true;
    setData(null); setError(null);
    backend.get("sii/ventas-clientes", { params: { meses } })
      .then(res => { if (vivo) setData(res.data); })
      .catch(e => { if (vivo) setError(e?.response?.data?.error || e.message); });
    return () => { vivo = false; };
  }, [meses]);

  if (error) return <div style={{ color: "#e74c3c", fontSize: 12, padding: 12 }}>Error: {error}</div>;
  if (!data) return <div style={{ color: "#8a8a8a", fontSize: 12, padding: 12 }}>Cargando ventas por cliente…</div>;

  const { periodos, totalGeneral, totalPorMes, clientes } = data;
  const top = clientes[0];
  const restoPct = top ? +(100 - top.pct).toFixed(1) : 0;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>

      {/* Selector de rango */}
      <div className="chips-scroll" style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={S.label}>Rango</span>
        {[6, 12, 24].map(m => (
          <button key={m} type="button" className="chip-touch no-touch-min t-02"
            onClick={() => setMeses(m)}
            style={{
              background: meses === m ? "#3a1114" : "#1e1e1e",
              color: meses === m ? "#ff8a8f" : "#999",
              border: `1px solid ${meses === m ? "#e30613" : "#333"}`,
              borderRadius: 6, padding: "4px 10px", fontSize: 11, cursor: "pointer",
            }}>
            {m} meses
          </button>
        ))}
      </div>

      {/* Resumen: total + concentración */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <div style={{ ...S.card, flex: 1, minWidth: 220 }}>
          <div style={S.label}>Facturado últimos {meses} meses (con IVA, NCs restadas)</div>
          <div style={{ fontSize: 24, fontWeight: 800, color: "#2ecc71", margin: "4px 0 10px", ...num }}>
            {fmtCLP(totalGeneral)}
          </div>
          <BarrasMensuales periodos={periodos} valores={totalPorMes} />
        </div>

        {top && (
          <div style={{ ...S.card, flex: 1, minWidth: 220 }}>
            <div style={S.label}>Concentración — riesgo cliente único</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: "#fff", margin: "4px 0 2px" }}>
              {top.razon}
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: top.pct >= 70 ? "#e74c3c" : "#f39c12", ...num }}>
              {top.pct}%
              <span style={{ fontSize: 12, fontWeight: 600, color: "#8a8a8a", marginLeft: 8 }}>
                del total · otros clientes {restoPct}%
              </span>
            </div>
            {/* Barra apilada top vs resto */}
            <div style={{ display: "flex", height: 10, borderRadius: 5, overflow: "hidden", marginTop: 10, background: "#161616" }}>
              <div style={{ width: `${Math.min(top.pct, 100)}%`, background: top.pct >= 70 ? "#e74c3c" : "#f39c12" }} />
              <div style={{ flex: 1, background: "#2ecc71", opacity: 0.7 }} />
            </div>
            <div style={{ fontSize: 10, color: "#8a8a8a", marginTop: 6 }}>
              {top.pct >= 70
                ? "Sobre 70%: dependencia alta — diversificar es prioridad."
                : "Bajo 70%: concentración en rango manejable."}
            </div>
          </div>
        )}
      </div>

      {/* Lista de clientes — tocar abre el perfil */}
      <div style={S.label}>Clientes por facturación ({clientes.length})</div>
      {clientes.map(c => {
        const sinComprar = mesesDesde(c.ultimaFecha);
        const inactivo = sinComprar != null && sinComprar >= 3;
        const esAbierto = abierto === c.rut;
        return (
          <div key={c.rut} className="t-02" style={{
            ...S.card, padding: 0, overflow: "hidden",
            border: `1px solid ${esAbierto ? "#3a5a7a" : "#2e2e2e"}`,
            background: esAbierto ? "#262626" : "#232323",
          }}>
            <div
              className={esAbierto ? "" : "tap-card"}
              onClick={() => setAbierto(esAbierto ? null : c.rut)}
              style={{ display: "flex", alignItems: "center", gap: 10, padding: "12px 14px", cursor: "pointer", minHeight: 48 }}
            >
              <span className="bini-chevron" style={{ transform: esAbierto ? "rotate(90deg)" : "none", flexShrink: 0 }}>›</span>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: "#fff", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {c.razon}
                </div>
                <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>
                  {c.rut} · {c.nDocs} docs
                  {c.ultimaFecha && (
                    <span style={{ color: inactivo ? "#e74c3c" : "#8a8a8a", fontWeight: inactivo ? 700 : 400 }}>
                      {" "}· última compra {new Date(c.ultimaFecha).toLocaleDateString("es-CL", { day: "2-digit", month: "short", year: "2-digit" })}
                      {inactivo ? ` (hace ${sinComprar} meses)` : ""}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ textAlign: "right", flexShrink: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 800, color: "#2ecc71", ...num }}>{fmtCLP(c.total)}</div>
                <div style={{ fontSize: 10, color: "#8a8a8a", ...num }}>{c.pct}%</div>
              </div>
            </div>
            {esAbierto && <PerfilCliente cliente={c} periodos={periodos} />}
          </div>
        );
      })}
    </div>
  );
};

export default VentasPorCliente;
